<template>
  <home-floating-window
    :title="$tc('Cluster', 1)"
    @close="escapeClusterWindow"
    @changePosition="$emit('changeClusterWindowsPosition', $event)"
    :pos-x="position.x"
    :pos-y="position.y"
    :draggable="!loading"
  >
    <template v-slot:iconWindows>
      <ClusterSVG class="floating-container-svg" />
    </template>

    <template v-slot:objectDivWindows>
      <div class="floating-tool-content">
        <div class="cluster-objects-div">
          <cluster-selection-type-buttons
            :selection-type="cluster.type ? cluster.type : 'polygon'"
            :creatable="cluster.creatable"
            @setType="setClusterType"
          />

          <cluster-selected-objects
            v-if="cluster.creatable"
            :selectedObjects="clusterWKT"
            :selectionType="cluster.type"
            @openObjectMenu="openClusterObjectMenu($event.event, $event.object)"
          />
          <HighlightedBox
            :bad-object="badObject"
            :is-visible="showInstruction"
            :message="currentInstruction"
          />
        </div>

        <cluster-fields-create
          v-if="cluster.creatable"
          :maxPathwayDistance="maxPathwayDistance"
          :maxCableLength="maxCableLength"
          @changeValue="changeValue($event)"
          :cluster-w-k-t="clusterWKT"
          :fields="dataSaveCluster"
        />
      </div>

      <home-window-button
        :label="clusterLabelButton"
        :disabled="clusterButtonDisabled"
        @buttonClick="changeCluster"
      />

      <window-object-menu
        v-if="clusterObjectMenu.open"
        :event="clusterObjectMenu.event"
        :object="clusterObjectMenu.object"
        :actionFrom="selectedToolObjectLayer"
        @updateGeoServer="updateGeoServer"
        @closeMenu="closeClusterObjectMenu"
        @openForm="openFormFromClusterObjectMenu"
        @openDetailForm="openFormFromClusterObjectMenu('detail')"
        @removeFromCable="removeObjectFromCluster"
        v-on-clickaway="closeClusterObjectMenu"
      />

      <cluster-closure-selection
        v-if="openClosureSelection"
        :closure-options="polygonClosures"
        @submit="previewFromClosureSelection"
        @close="openClosureSelection = false"
      />

      <app-dialog-box
        :open="clearDialog"
        :title="$t('ClusterCreationClear')"
        :content="
          $t('ClusterCreationWarning') +
            '\n\n' +
            $t('ClusterCreationConfirmation')
        "
        :main-action-label="$t('No')"
        @mainAction="clearDialog = false"
        :secondary-action-label="$t('Yes')"
        @secondaryAction="clearCluster"
      />

      <app-dialog-box
        :open="closeDialog"
        :title="$t('ClusterCreationLeave')"
        :content="
          $t('ClusterCreationWarning') +
            '\n\n' +
            $t('ClusterCreationConfirmation')
        "
        :main-action-label="$t('No')"
        @mainAction="closeDialog = false"
        :secondary-action-label="$t('Yes')"
        @secondaryAction="closeCluster"
      />

      <loading-spinner
        :active.sync="loading"
        :is-full-page="true"
        color="#4fa2b7"
        :height="30"
        :width="30"

      />
    </template>
  </home-floating-window>
</template>

<script>
import { bus } from "@/main";
import { mapActions } from "vuex";
import { Circle as CircleStyle, Fill, Stroke, Style } from "ol/style";
import { GeoJSON } from "ol/format";
import { mixin as clickaway } from "vue-clickaway";
import LoadingSpinner from "vue-loading-overlay";
import ClusterSVG from "@/assets/cluster_svgs/cluster_window.svg";
import WindowHeaderButton from "@/components/_Atoms/WindowHeaderButton";
import HomeWindowButton from "../../HomeWindowButton";
import WindowObjectMenu from "../../ObjectMenu/WindowObjectMenu";
import AppDialogBox from "@/components/_Molecules/AppDialogBox";
import HomeFloatingWindow from "../HomeFloatingWindow";
import ClusterSelectionTypeButtons from "@/components/Home/Content/ObjectWindow/Cluster/SelectionTypeButtons";
import ClusterSelectedObjects from "@/components/Home/Content/ObjectWindow/Cluster/SelectedObjects";
import ClusterClosureSelection from "@/components/Home/Content/ObjectWindow/Cluster/ClosureSelection";
import HighlightedBox from "@/components/_Molecules/HighlightedBox";
import ClusterFieldsCreate from "@/components/Home/Content/ObjectWindow/Cluster/ClusterFieldsCreate";
import { clusterOptionsMixin } from "@/mixins/clusterOptionsMixin.js";


export default {
  name: "ClusterWindow",

  components: {
    ClusterFieldsCreate,
    HighlightedBox,
    HomeFloatingWindow,
    ClusterClosureSelection,
    ClusterSelectedObjects,
    ClusterSelectionTypeButtons,
    AppDialogBox,
    ClusterSVG,
    WindowHeaderButton,
    HomeWindowButton,
    WindowObjectMenu,
    LoadingSpinner
  },

  mixins: [clickaway, clusterOptionsMixin],

  props: {
    cluster: {
      type: Object
    },
    clusterWKT: {
      type: Array
    },
    vectorSource: {},
    position: Object
  },

  data() {
    return {
      dataSaveCluster: {
        cable_owner: 1,
        cable_material_reference: null,
        pathway_type: null,
        pathway_material_reference: null,
        pathway_owner: 1,
        filter_pathways_with_ignore: false,
        filter_bd_include: false,
      },
      clusterObjectMenu: {
        open: false,
        event: null,
        object: null
      },
      maxPathwayDistance: 0,
      maxCableLength: 0,

      statusWorkspace: null,

      polygonClosures: null,
      openClosureSelection: false,

      badObject: false,

      buttonLabel: "create",

      clearDialog: false,
      closeDialog: false,

      loading: false,

      local_storage_name: "lastCluster"
    };
  },

  computed: {
    currentInstruction() {
      let instruction = "";
      switch (this.cluster.type) {
        case null:
          instruction = this.$t("SelectClusterType");

          break;
        case "point":
          if (this.clusterWKT.length === 0) {
            instruction = this.$t("SelectClosure");
          }
          if (this.clusterWKT.length > 0) {
            instruction = this.$t("SelectBuilding");
          }
          break;
        case "polygon":
          if (this.clusterWKT.length === 0) {
            instruction = this.$t("DrawClusterPolygon");
          }
          break;
      }
      return instruction;
    },

    clusterLabelButton() {
      if (this.buttonLabel === "create") {
        return this.$t("Create");
      } else if (this.buttonLabel === "edit") {
        return this.$t("Edit");
      } else if (this.buttonLabel === "delete") {
        return this.$t("Delete");
      }
    },

    clusterButtonDisabled() {
      return (
        !this.cluster.creatable ||
        this.maxPathwayDistance < 0 ||
        this.maxCableLength < 0 ||
        !this.dataSaveCluster.cable_material_reference ||
        !this.dataSaveCluster.cable_owner ||
        !this.dataSaveCluster.pathway_owner ||
        !this.dataSaveCluster.pathway_type
      );
    }
  },

  mounted() {
    this.cluster.type = "polygon";

    this.getWorkspacePhaseValue().then(data => {
      this.statusWorkspace = data;
    });

    this.setAutomaticPathwayFieldValues();

    bus.$on("add-new-point-to-cluster", data =>
      this.addNewPointToCluster(data.uuid, data.objectTypeRoute)
    );
    bus.$on("start-cluster-by-polygon", e => this.startClusterByPolygon(e));
    bus.$emit("highlight-cluster-box", () => this.highlightClusterBox());

    bus.$on("escape-cluster-window", () => this.escapeClusterWindow());
    bus.$on("deactivate-cluster-creation", () => this.escapeClusterWindow());
  },

  destroyed() {
    bus.$off("add-new-point-to-cluster");
    bus.$off("start-cluster-by-polygon");
    bus.$off("escape-cluster-window");
    bus.$off("deactivate-cluster-creation");
  },

  methods: {
    ...mapActions(["CREATE_CLUSTER", "GET_CLUSTER_OBJECTS"]),

    setAutomaticPathwayFieldValues() {
      this.maxPathwayDistance = this.$store.getters.getClusterMaxPathwayDistance;
      this.maxCableLength = this.$store.getters.getClusterMaxCableLength;
    },

    escapeClusterWindow() {
      if (
        this.dataSaveCluster.pathway_type === null &&
        this.dataSaveCluster.cable_material_reference === null
      ) {
        this.closeCluster();
      } else {
        this.clearDialog = true;
      }
    },

    clearCluster() {
      this.clearDialog = false;
      this.closeCluster();
    },

    closeCluster() {
      this.$emit("close");
    },

    setClusterType(typeOfSelection) {
      this.cluster.type = typeOfSelection;
      this.resetCluster(true);
      this.clearDataSaveCluster();
    },

    changeCluster() {
      this.prepareParametersValues();
      this.loading = true;
      this.CREATE_CLUSTER(this.dataSaveCluster)
        .then(() => {
          sessionStorage.setItem(
            this.local_storage_name,
            JSON.stringify(this.dataSaveCluster)
          );
          this.updateGeoServer();
          this.clearDataSaveCluster();
        })
        .catch(error => {
          if (error && error.response && error.response.data) {
            this.$toasted.global.toast_error(error.response.data.data);
          }
        })
        .then(() => {
          this.loading = false;
        });

      this.resetCluster(true);
    },

    prepareParametersValues() {
      if (!this.maxPathwayDistance || this.maxPathwayDistance === "") {
        this.maxPathwayDistance = 0;
      }
      this.dataSaveCluster.max_pathway_distance = this.maxPathwayDistance;

      if (!this.maxCableLength || this.maxCableLength === "") {
        this.maxCableLength = 0;
      }
      this.dataSaveCluster.max_cable_length = this.maxCableLength;
      this.dataSaveCluster.status = this.statusWorkspace;
    },

    resetCluster(unbindEvent) {
      this.$emit("resetCluster", unbindEvent);
    },

    startClusterByPolygon(event) {
      this.resetCluster(false);
      this.$emit("drawNewPolygon", event, promise => {
        promise.then(polygonWKT => {
          this.dataSaveCluster.text_geom = polygonWKT;
          this.dataSaveCluster.projection = 3857;

          this.previewCluster();
        });
      });
    },

    previewFromClosureSelection(selectedClosureUUID) {
      this.dataSaveCluster.closure_uuid = selectedClosureUUID;
      this.previewCluster();
    },

    previewCluster() {
      this.loading=true;
      this.GET_CLUSTER_OBJECTS(this.dataSaveCluster)
        .then(data => {
          this.cluster.creatable = true;
          let fields = data.closure;
          this.dataSaveCluster.cable_owner = fields.cl_owner
            ? fields.cl_owner
            : 1;
          this.dataSaveCluster.cable_material_reference =
            fields.cable_material_reference;
          this.dataSaveCluster.pathway_type = fields.pathway_type;
          this.dataSaveCluster.pathway_material_reference =
            fields.pathway_material_reference;
          this.dataSaveCluster.pathway_owner = fields.pathway_owner
            ? fields.pathway_owner
            : 1;

          this.addClusterHighlight(data);
          this.addInfluenceLinesHighlight(data);
          this.addNodesToClusterPreview(data);

          this.scrollClusterWindow();
        })
        .catch(error => {
          if (
            this.cluster.type === "polygon" &&
            error.data &&
            error.data.closures
          ) {
            this.polygonClosures = error.data.closures;
            this.openClosureSelection = true;
            this.resetCluster(true);
          } else {
            let message = error.data;
            this.$toasted.global.toast_error(message);
            if (
              this.cluster.type === "point" &&
              ((message.match(/building/i) && message.match(/closure/i)) ||
                message.match(/hp/i))
            ) {
              this.dataSaveCluster.buildings.pop();
              this.previewCluster();
            } else {
              this.clearDataSaveCluster();
            }
          }
        })
        .then(() => {
          this.loading = false;
        });

      this.resetCluster(true);
    },

    clearDataSaveCluster() {
      this.dataSaveCluster = {
        cable_owner: 1,
        cable_material_reference: null,
        pathway_type: null,
        pathway_material_reference: null,
        pathway_owner: 1
      };
    },

    addClusterHighlight(data) {
      let cluster = data.fiber_terminal_box;
      if (cluster) {
        this.setClusterWindowButtonLabel(cluster);

        let clusterJSON = cluster.ftbz_geo_json;
        let features = new GeoJSON().readFeatures(clusterJSON, {
          featureProjection: "EPSG:3857"
        });

        let feature = features[0];
        feature.setStyle(
          new Style({
            fill: new Fill({ color: "rgba(255, 0, 0, 0.3)" }),
            stroke: new Stroke({ color: "#bada55", width: 1 })
          })
        );
        this.vectorSource.addFeature(feature);
      } else {
        this.buttonLabel = "delete";
      }
    },

    setClusterWindowButtonLabel(cluster) {
      if (cluster.ftbz_uuid) {
        this.buttonLabel = "edit";
      } else {
        this.buttonLabel = "create";
      }
    },

    addInfluenceLinesHighlight(data) {
      let influenceLinesJSON = data.influence_lines;
      influenceLinesJSON.forEach(influenceLineJSON => {
        let features = new GeoJSON().readFeatures(
          influenceLineJSON.il_geo_json,
          { featureProjection: "EPSG:3857" }
        );
        let feature = features[0];
        feature.setStyle(
          new Style({
            stroke: new Stroke({ color: "#bada55", width: 3 })
          })
        );
        this.vectorSource.addFeature(feature);
      });
    },

    addNodesToClusterPreview(data) {
      let cl_bd_style = new Style({
        image: new CircleStyle({
          radius: 5,
          fill: new Fill({ color: "rgba(255, 0, 0, 0.5)" }),
          stroke: new Stroke({ color: "#bada55", width: 1 })
        })
      });

      this.addClosureToClusterPreview(cl_bd_style, data);
      this.addBuildingsToClusterPreview(cl_bd_style, data);
    },

    addClosureToClusterPreview(style, data) {
      let closure = data.closure;
      let technicalPoint = closure.technical_point
        ? closure.technical_point
        : closure.technicalpoint;

      this.addClosureToClusterWKT(closure, technicalPoint);
      this.highlightClosure(technicalPoint, style);
    },

    async addClosureToClusterWKT(closure, technicalPoint) {
      let maxHP = closure.cl_max_hp;
      if (closure.cl_material_id) {
        maxHP = await this.getMaxHpsFromClosureMaterial(closure);
      }

      this.clusterWKT.push({
        type: "Closure",
        uuid: closure.cl_uuid,
        id: closure.cl_id,
        text_geom: technicalPoint.node.nd_text_geom,
        endpoint: "closures",
        maxHP: maxHP,
        hover: false,
        label: closure.cl_label,
        layerName: "fc_closure"
      });
    },

    highlightClosure(technicalPoint, style) {
      let closureGeoJSON = technicalPoint.node.nd_geo_json;
      closureGeoJSON = closureGeoJSON.replace(/'/g, '"');
      let features = new GeoJSON().readFeatures(closureGeoJSON, {
        featureProjection: "EPSG:3857"
      });
      let feature = features[0];
      feature.setStyle(style);
      this.vectorSource.addFeature(feature);
    },

    addBuildingsToClusterPreview(style, data) {
      let buildings = data.buildings;
      for (const building of buildings) {
        let technicalSite = building.technical_site
          ? building.technical_site
          : building.technicalsite;

        this.addBuildingToClusterWKT(building, technicalSite);
        this.highlightBuilding(technicalSite, style);
      }
    },

    addBuildingToClusterWKT(building, technicalSite) {
      let residentialHPs = building.bd_demand_point_hab_bals;
      let commercialHPs = building.bd_demand_point_com_bals;
      let max_hp =
        (residentialHPs !== null ? residentialHPs : 1) +
        (commercialHPs !== null ? commercialHPs : 1);

      this.clusterWKT.push({
        type: "Building",
        uuid: building.bd_uuid,
        id: building.bd_id,
        text_geom: technicalSite.node.nd_text_geom,
        endpoint: "buildings",
        max_hp: max_hp,
        hover: false,
        layerName: "fc_building"
      });
    },

    highlightBuilding(technicalSite, style) {
      let buildingGeoJSON = technicalSite.node.nd_geo_json;
      let features = new GeoJSON().readFeatures(buildingGeoJSON, {
        featureProjection: "EPSG:3857"
      });
      let feature = features[0];
      feature.setStyle(style);
      this.vectorSource.addFeature(feature);
    },

    scrollClusterWindow() {
      let localStorageData = JSON.parse(
        sessionStorage.getItem(this.local_storage_name)
      );

      if (localStorageData) {
        this.dataSaveCluster.cable_owner = localStorageData.cable_owner;
        this.dataSaveCluster.cable_material_reference =
          localStorageData.cable_material_reference;
        this.dataSaveCluster.pathway_type = localStorageData.pathway_type;
        this.dataSaveCluster.pathway_material_reference =
          localStorageData.pathway_material_reference;
        this.dataSaveCluster.pathway_owner = localStorageData.pathway_owner;
      }

      this.$nextTick(() => {
        let container = this.$el.querySelector(".cluster-objects-div");
        container.scrollTop = container.scrollHeight;
      });
    },

    addNewPointToCluster(uuid, objectTypeRoute) {
      let lastObject = this.clusterWKT[this.clusterWKT.length - 1];
      let currentObject = {
        type: objectTypeRoute,
        uuid: uuid
      };

      if (lastObject === undefined && currentObject.type !== "closure") {
        this.$toasted.global.toast_error("Select a closure first");
        return;
      }

      if (lastObject !== undefined && currentObject.type !== "building") {
        this.$toasted.global.toast_error("You can only select buildings");
        return;
      }

      if (currentObject.type === "closure") {
        this.dataSaveCluster.closure = { uuid: currentObject.uuid };
        this.dataSaveCluster.buildings = [];
      } else if (currentObject.type === "building") {
        let buildingIndex = this.dataSaveCluster.buildings.findIndex(
          building => building.uuid === uuid
        );
        if (buildingIndex === -1) {
          this.dataSaveCluster.buildings.push({ uuid: currentObject.uuid });
        } else {
          this.dataSaveCluster.buildings.splice(buildingIndex, 1);
        }
      }

      this.resetCluster(false);
      this.previewCluster();
    },

    addOrRemoveBuildingFromCluster(uuid) {
      let buildingIndex = this.dataSaveCluster.buildings.findIndex(
        building => building.uuid === uuid
      );
      if (buildingIndex === -1) {
        this.dataSaveCluster.buildings.push({ uuid: uuid });
      } else {
        this.dataSaveCluster.buildings.splice(buildingIndex, 1);
      }
    },

    removeObjectFromCluster(uuid) {
      this.closeClusterObjectMenu();
      let index = this.clusterWKT.findIndex(point => point.uuid === uuid);
      let isBuilding = this.clusterWKT[index].type === "Building";
      if (index > -1) {
        if (isBuilding) {
          this.addOrRemoveBuildingFromCluster(uuid);
        }

        this.resetCluster(false);
        if (isBuilding) {
          this.previewCluster();
        }
      }
    },

    highlightClusterBox() {
      if (this.cluster.type === "point" || this.cluster.type === null) {
        this.badObject = true;
        setTimeout(() => {
          this.badObject = false;
        }, 550);
      }
    }
  }
};
</script>

<style scoped>
input:invalid,
textarea:invalid {
  border: 1px solid var(--red);
}

.cluster-objects-div {
  padding: 5px 10px;
  min-height: 130px;
  min-width: 230px;
}

</style>



// WEBPACK FOOTER //
// src/components/Home/Content/ObjectWindow/Cluster/ClusterWindow.vue